import {
  SkyscannerHotelunifiedbffHotelatypicaldealsV1HotelAtypicalDeals,
  SkyscannerHotelunifiedbffHotelatypicaldealsV1QualityStay,
  SkyscannerHotelunifiedbffHotelpriceV1HotelPrice,
  SkyscannerHotelunifiedbffHotelreviewV1HotelReview,
  SkyscannerHotelunifiedbffHotelsearchV1HotelSearch,
  SkyscannerHotelunifiedbffLinktransformV1LinkTransform,
  SkyscannerHotelunifiedbffTranslationV1MachineTranslation,
  SkyscannerHotelunifiedbffHotelcontentV1HotelContent,
  SkyscannerHotelunifiedbffHotelopentextsearchV1HotelOpenTextSearch,
  SkyscannerHotelunifiedbffNeighbourhoodsnapshotsV1NeighbourhoodSnapshots,
} from '@skyscanner-internal/hotel-unified-bff-web-http-client';

import { getCashBackUserId } from '../cash-back-user-id';

import { getCommonHeaders } from './build-params';
import instrumentedFrisbeeClientFetch from './instrumented-frisbee-client-fetch';

import type {
  GetNeighbourhoodSnapshotsRequest,
  GetNeighbourhoodSnapshotsResponse,
} from '@shared/types/frontend/ubff/neighbourhood-snapshots';
import type {
  ErrorHandledResponse,
  UBFFGateway as UBFFGatewayType,
} from '@shared/types/frontend/ubff-gateway';
import type { UserContext } from '@shared/types/user-context';
import type { Optional } from '@shared/types/utils';
import type {
  GetPriceBandRequest,
  GetPriceBandsRequest,
} from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelatypicaldeals/v1/hotel_atypical_deals.pb';
import type { GetCarouselHotelsRequest } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelatypicaldeals/v1/quality_stay.pb';
import type { GetHotelContentRequestFieldMaskWithRange } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelcontent/v1/hotel_content.pb';
import type {
  RecognizeHotelSearchIntentsRequest,
  GetHotelAISuggestionsRequest,
} from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelopentextsearch/v1/hotel_open_text_search.pb';
import type { GetHotelReviewsRequest } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelreview/v1/hotel_review.pb';
import type { GetTransformedLinkRequest } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/linktransform/v1/link_transform.pb';
import type { GetTranslationRequest } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/translation/v1/machine_translation.pb';

import {
  BREAK_POLLING_CODES,
  MIN_POLL_INTERVAL,
  PATH_PRE_FIX,
  SKYSCANNER_CORRELATION_ID,
} from '@shared/constants/ubff-gateway';
import poll from '@shared/utils-universal/poll';

type Props = {
  userContext: UserContext;
};

const ubffPollingShouldContinue =
  <Resp>(apiShouldContinue: (response: Resp) => boolean) =>
  async (
    fetchResult: Promise<ErrorHandledResponse<Resp>>,
    forceComplete: boolean,
  ) => {
    if (forceComplete) {
      return false;
    }
    const { error, response } = await fetchResult;
    const errCode = error?.code;
    if (errCode && BREAK_POLLING_CODES.includes(errCode)) {
      return false;
    }
    if (!response) {
      // Response is null and errorCode not in BREAK_POLLING_CODES, we should continue polling
      return true;
    }
    return apiShouldContinue(response);
  };

const UBFFGateway = ({
  userContext: { device, impressionId, ipAddress, isWebView },
}: Props): UBFFGatewayType => ({
  pollHotelOffers: async ({ ...requestContext }) => {
    let sessionId: Optional<string>;
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const getHotelDetailPrice = async () => {
      const { HotelPriceService } =
        SkyscannerHotelunifiedbffHotelpriceV1HotelPrice;
      const fetch = instrumentedFrisbeeClientFetch({
        func: HotelPriceService.GetHotelDetailPrice,
        correlationId: headers[SKYSCANNER_CORRELATION_ID],
      });

      const fetchResult = await fetch(
        {
          ...requestContext,
          sessionId,
        },
        {
          pathPrefix: PATH_PRE_FIX,
          headers,
        },
      );
      sessionId = fetchResult.response?.sessionId;
      return fetchResult;
    };

    const fetchResult = await poll(
      getHotelDetailPrice,
      ubffPollingShouldContinue(
        (response) => response.pollingStatus !== 'SEARCH_STATUS_COMPLETED',
      ),
      MIN_POLL_INTERVAL,
    );
    return fetchResult;
  },

  pollHotelSearch: async ({ ...requestContext }) => {
    let { sessionToken } = requestContext;
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
      fromCashBack: !!getCashBackUserId(),
    });

    const getHotelSearch = async () => {
      const { HotelSearchService } =
        SkyscannerHotelunifiedbffHotelsearchV1HotelSearch;
      const fetch = instrumentedFrisbeeClientFetch({
        func: HotelSearchService.GetHotelSearch,
        correlationId: headers[SKYSCANNER_CORRELATION_ID],
      });

      const fetchResult = await fetch(
        {
          ...requestContext,
          sessionToken,
        },
        {
          pathPrefix: PATH_PRE_FIX,
          headers,
        },
      );
      sessionToken = fetchResult.response?.sessionToken;
      return fetchResult;
    };

    const fetchResult = await poll(
      getHotelSearch,
      ubffPollingShouldContinue(
        (response) => response.searchStatus !== 'SEARCH_STATUS_COMPLETED',
      ),
      MIN_POLL_INTERVAL,
    );
    return fetchResult;
  },

  getHotelImages: async (params) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { HotelContentService } =
      SkyscannerHotelunifiedbffHotelcontentV1HotelContent;

    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelContentService.GetHotelContent,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const paths: GetHotelContentRequestFieldMaskWithRange = {
      paths: [
        { path: 'PATH_GALLERY_CATEGORY' },
        { path: 'PATH_GALLERY_IMAGE' },
        { path: 'PATH_REVIEW_IMAGE' },
        { path: 'PATH_GALLERY_VIDEO' },
        { path: 'PATH_IMAGE_COUNT' },
      ],
    };

    const fetchResult = await fetch(
      { ...params, requestedFields: paths },
      {
        pathPrefix: PATH_PRE_FIX,
        headers,
      },
    );

    return fetchResult.response;
  },

  getPriceBand: async (params: GetPriceBandRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { HotelAtypicalDealsService } =
      SkyscannerHotelunifiedbffHotelatypicaldealsV1HotelAtypicalDeals;

    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelAtypicalDealsService.GetPriceBand,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult.response;
  },

  getPriceBands: async (params: GetPriceBandsRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { HotelAtypicalDealsService } =
      SkyscannerHotelunifiedbffHotelatypicaldealsV1HotelAtypicalDeals;

    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelAtypicalDealsService.GetPriceBands,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult.response;
  },

  getHotelReviews: async (params: GetHotelReviewsRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });
    const { HotelReviewService } =
      SkyscannerHotelunifiedbffHotelreviewV1HotelReview;
    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelReviewService.GetHotelReviews,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult;
  },

  getTranslation: async (params: GetTranslationRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { TranslationService } =
      SkyscannerHotelunifiedbffTranslationV1MachineTranslation;

    const fetch = instrumentedFrisbeeClientFetch({
      func: TranslationService.GetTranslation,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const response = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return response;
  },

  getIntents: async (params: RecognizeHotelSearchIntentsRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { HotelOpenTextSearchService } =
      SkyscannerHotelunifiedbffHotelopentextsearchV1HotelOpenTextSearch;

    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelOpenTextSearchService.RecognizeHotelSearchIntents,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult;
  },

  getAISuggestions: async (params: GetHotelAISuggestionsRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { HotelOpenTextSearchService } =
      SkyscannerHotelunifiedbffHotelopentextsearchV1HotelOpenTextSearch;

    const fetch = instrumentedFrisbeeClientFetch({
      func: HotelOpenTextSearchService.GetHotelAISuggestions,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult.response;
  },

  getCarouselHotels: async (params: GetCarouselHotelsRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { QualityStayService } =
      SkyscannerHotelunifiedbffHotelatypicaldealsV1QualityStay;

    const fetch = instrumentedFrisbeeClientFetch({
      func: QualityStayService.GetCarouselHotels,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult.response;
  },

  // CopyPasteCard API: transforms a partner hotel URL or screenshot image into a Skyscanner URL
  linkTransform: async (request: GetTransformedLinkRequest) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { LinkTransformService } =
      SkyscannerHotelunifiedbffLinktransformV1LinkTransform;

    const fetch = instrumentedFrisbeeClientFetch({
      func: LinkTransformService.GetTransformedLink,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    return fetch(request, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });
  },

  getNeighbourhoodSnapshots: async (
    params: GetNeighbourhoodSnapshotsRequest,
  ) => {
    const headers = getCommonHeaders({
      device,
      ipAddress,
      isWebView,
      impressionId,
    });

    const { NeighbourhoodSnapshotsService } =
      SkyscannerHotelunifiedbffNeighbourhoodsnapshotsV1NeighbourhoodSnapshots;

    const fetch = instrumentedFrisbeeClientFetch({
      func: NeighbourhoodSnapshotsService.GetNeighbourhoodSnapshots,
      correlationId: headers[SKYSCANNER_CORRELATION_ID],
    });

    const fetchResult = await fetch(params, {
      pathPrefix: PATH_PRE_FIX,
      headers,
    });

    return fetchResult as ErrorHandledResponse<GetNeighbourhoodSnapshotsResponse>;
  },
});

export default UBFFGateway;
